/* Interactive, storefront-only FUTPilot 1.1.3 Bidding editor preview. */
(function () {
  'use strict';

  const MAX_FILTERS = 15;
  const GLYPHS = { add: '\ue0c3', open: '\ue092', save: '\ue0c4', revert: '\ue0c8', help: '\ue0b9', delete: '\ue093', confirm: '\ue02f', close: '\ue0b8' };
  const clone = value => JSON.parse(JSON.stringify(value));
  const makeId = () => globalThis.crypto?.randomUUID?.() || 'bid-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  const PRICE_BANDS = [[0, 1000, 50], [1001, 10000, 100], [10001, 50000, 250], [50001, 100000, 500], [100001, Infinity, 1000]];
  function adjacentPrice(value, direction) {
    const amount = Math.max(0, Number(value) || 0);
    const choices = PRICE_BANDS.map(([min, max, step]) => {
      const first = Math.ceil(min / step) * step;
      const last = Number.isFinite(max) ? Math.floor(max / step) * step : Infinity;
      const candidate = direction > 0 ? Math.floor(amount / step) * step + step : Math.ceil(amount / step) * step - step;
      return Math.min(last, Math.max(first, candidate));
    }).filter(next => direction > 0 ? next > amount : next < amount);
    return choices.length ? direction > 0 ? Math.min(...choices) : Math.max(...choices) : 0;
  }
  function closestPrice(value) {
    const amount = Math.max(0, Number(value) || 0);
    return PRICE_BANDS.map(([min, max, step]) =>
      Math.min(Number.isFinite(max) ? Math.floor(max / step) * step : Infinity,
        Math.max(Math.ceil(min / step) * step, Math.round(amount / step) * step)))
      .reduce((best, next) => Math.abs(next - amount) < Math.abs(best - amount) ||
        (Math.abs(next - amount) === Math.abs(best - amount) && next > best) ? next : best);
  }
  const el = (tag, className, text) => { const node = document.createElement(tag); if (className) node.className = className; if (text !== undefined) node.textContent = text; return node; };
  const icon = name => { const node = el('span', 'afp-icon', GLYPHS[name]); node.setAttribute('aria-hidden', 'true'); return node; };
  const button = (label, glyph, click, className) => { const node = el('button', 'afp-button' + (className ? ' ' + className : '')); node.type = 'button'; if (glyph) node.append(icon(glyph)); if (label) node.append(document.createTextNode(label)); if (click) node.addEventListener('click', click); return node; };
  function defaultState() {
    return {
      schemaVersion: 3,
      profiles: [{
        id: 'profile-1', name: 'First filter', enabled: true, order: 0,
        filter: null, playerRating: null, maxTimeRemainingSeconds: 300,
        priceMode: 'fixed', maxBid: 0, discountPercent: 5, bidRules: [],
        bidLimit: 10, wonLimit: 0, bidGapMs: 1000, pageLimit: 0
      }],
      loop: {
        runDurationSeconds: 3600, runDurationMinValue: 0, runDurationMaxValue: 0,
        runDurationUnit: 'minute', maxActiveAuctions: 100,
        wonAction: 'transferList', retainedWinsCap: 10, minimumCoins: 0,
        quoteMaxAgeMs: 300000, idleDelayMs: 5000
      }
    };
  }
  function normalize(value) {
    const defaults = defaultState();
    if (!value || typeof value !== 'object' || !Array.isArray(value.profiles) || !value.profiles.length) return defaults;
    const plan = clone(value);
    plan.schemaVersion = 3;
    plan.profiles = plan.profiles.slice(0, MAX_FILTERS).map((profile, index) => ({
      ...clone(defaults.profiles[0]), ...profile,
      id: String(profile.id || makeId()), name: String(profile.name || `Filter ${index + 1}`),
      enabled: profile.enabled !== false, order: index,
      bidRules: Array.isArray(profile.bidRules) ? profile.bidRules : []
    }));
    plan.loop = { ...defaults.loop, ...(plan.loop && typeof plan.loop === 'object' ? plan.loop : {}) };
    return plan;
  }
  function mount(container, options) {
    if (!(container instanceof Element)) throw new Error('Bidding preview container is missing.');
    options = options || {};
    const screen = container.classList.contains('af-demo-screen-bidding') ? container : container.closest('.af-demo-screen-bidding');
    const host = screen || container;
    const saved = normalize(options.getStored?.('bidding'));
    let data = clone(saved);
    let snapshot = clone(saved);
    let selectedId = data.profiles[0].id;
    let activeTab = 'filters';
    let helpVisible = false;
    let deleted = null;
    let status = '';
    let error = '';
    let modal = null;
    let modalFocus = null;
    let savedPulse = 0;
    const disclosures = { pricing: false, rules: false };
    const shell = host.closest('.af-demo-shell') || host;

    const selected = () => data.profiles.find(profile => profile.id === selectedId) || data.profiles[0];
    const hasUnsaved = () => JSON.stringify(data) !== JSON.stringify(snapshot);
    const activeKey = () => typeof options.getActiveKey === 'function' ? options.getActiveKey('bidding') : null;
    function feedback(text, tone) {
      status = tone === 'error' ? '' : text;
      error = tone === 'error' ? text : '';
      if (typeof options.onFeedback === 'function') options.onFeedback(text, tone || 'info');
      const node = host.querySelector('.afp-bid-feedback');
      if (node) { node.textContent = text; node.dataset.tone = tone || 'info'; }
    }
    function changed(next, rerender = true) {
      data = next;
      status = '';
      error = '';
      savedPulse = 0;
      if (rerender) render();
    }
    function updateFilter(key, value, rerender = true) {
      const next = clone(data);
      const filter = next.profiles.find(profile => profile.id === selectedId) || next.profiles[0];
      filter[key] = value;
      changed(next, rerender);
    }
    function updateLoop(key, value, rerender = true) {
      const next = clone(data);
      next.loop[key] = value;
      changed(next, rerender);
    }
    function closeModal() {
      if (!modal) return;
      modal.remove(); modal = null;
      if (modalFocus?.isConnected) modalFocus.focus();
      modalFocus = null;
    }
    function dialog(title, description, fields, confirmLabel, onConfirm) {
      closeModal();
      modalFocus = document.activeElement;
      const backdrop = el('div', 'afp-modal-backdrop');
      const panel = el('section', 'afp-modal');
      panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-modal', 'true');
      const heading = el('h2', '', title); heading.id = 'afp-bid-dialog-' + makeId();
      panel.setAttribute('aria-labelledby', heading.id);
      panel.append(heading, el('p', '', description));
      const controls = {};
      for (const field of fields || []) {
        const label = el('label', 'afp-field', field.label);
        const input = el('input', 'afp-input');
        input.type = field.type || 'text';
        if (field.type === 'number') input.min = '0';
        input.value = field.value == null ? '' : String(field.value);
        if (field.stepper === 'price') {
          const stepper = el('span', 'afp-bid-stepper');
          const adjust = direction => { input.value = String(adjacentPrice(input.value, direction)); };
          const minus = button('−', null, () => adjust(-1)); minus.setAttribute('aria-label', `Decrease ${field.label}`);
          const plus = button('+', null, () => adjust(1)); plus.setAttribute('aria-label', `Increase ${field.label}`);
          minus.addEventListener('pointerdown', event => event.preventDefault());
          plus.addEventListener('pointerdown', event => event.preventDefault());
          input.addEventListener('keydown', event => {
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') { event.preventDefault(); adjust(event.key === 'ArrowUp' ? 1 : -1); }
          });
          input.addEventListener('blur', () => { if (input.value !== '') input.value = String(closestPrice(input.value)); });
          stepper.append(minus, input, plus); label.append(stepper);
        } else label.append(input);
        panel.append(label); controls[field.key] = input;
      }
      const message = el('p', 'afp-bid-modal-error'); message.setAttribute('role', 'alert'); message.hidden = true; panel.append(message);
      const actions = el('div', 'afp-modal-actions');
      const accept = button(confirmLabel, 'confirm', async () => {
        const values = Object.fromEntries(Object.entries(controls).map(([key, input]) => [key, input.value]));
        try { accept.disabled = true; await onConfirm(values); closeModal(); render(); }
        catch (issue) { message.textContent = issue?.message || 'Could not complete this action.'; message.hidden = false; accept.disabled = false; Object.values(controls)[0]?.focus(); }
      }, 'afp-button-primary');
      const cancel = button('Cancel', 'close', closeModal);
      actions.append(accept, cancel); panel.append(actions); backdrop.append(panel); shell.append(backdrop); modal = backdrop;
      backdrop.addEventListener('click', event => { if (event.target === backdrop) closeModal(); });
      backdrop.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); closeModal(); }
        if (event.key === 'Tab') {
          const focusables = [...panel.querySelectorAll('input,button')].filter(node => !node.disabled);
          const index = focusables.indexOf(document.activeElement);
          if (event.shiftKey && index === 0) { event.preventDefault(); focusables.at(-1)?.focus(); }
          else if (!event.shiftKey && index === focusables.length - 1) { event.preventDefault(); focusables[0]?.focus(); }
        }
      });
      (Object.values(controls)[0] || accept).focus();
    }
    function field(label, value, onChange, details = {}) {
      const wrap = el('label', 'afp-field');
      wrap.append(el('span', '', label));
      let input;
      if (details.options) {
        input = el('select', 'afp-select');
        for (const [optionValue, optionLabel] of details.options) {
          const option = el('option', '', optionLabel); option.value = optionValue; input.append(option);
        }
        input.value = String(value ?? '');
        input.addEventListener('change', () => onChange(input.value, true));
      } else {
        input = el('input', 'afp-input');
        input.type = details.type || 'number';
        if (input.type === 'number') { input.min = String(details.min ?? 0); input.step = details.step || '1'; }
        input.value = value == null ? '' : String(value);
        input.addEventListener('input', () => onChange(input.value, false));
      }
      input.setAttribute('aria-label', label);
      wrap.append(input);
      if (details.help) wrap.append(el('small', 'afp-bid-help', details.help));
      return wrap;
    }
    function stepField(label, value, onChange, kind = 'price') {
      const wrap = el('label', 'afp-field'); wrap.append(el('span', '', label));
      const control = el('span', 'afp-bid-stepper');
      const input = el('input', 'afp-input'); input.type = 'number'; input.min = kind === 'rating' ? '1' : '0';
      if (kind === 'rating') input.max = '99';
      input.value = value == null ? '' : String(value); input.setAttribute('aria-label', label);
      const step = direction => {
        const next = kind === 'rating'
          ? (direction < 0 && Number(input.value) <= 1 ? '' : String(Math.max(1, Math.min(99, (Number(input.value) || 0) + direction))))
          : String(adjacentPrice(input.value, direction));
        input.value = next; onChange(next, true);
      };
      const decrement = button('−', null, () => step(-1)); decrement.setAttribute('aria-label', `Decrease ${label}`);
      const increment = button('+', null, () => step(1)); increment.setAttribute('aria-label', `Increase ${label}`);
      decrement.addEventListener('pointerdown', event => event.preventDefault());
      increment.addEventListener('pointerdown', event => event.preventDefault());
      input.addEventListener('input', () => onChange(input.value, false));
      input.addEventListener('keydown', event => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        event.preventDefault(); step(event.key === 'ArrowUp' ? 1 : -1);
      });
      input.addEventListener('blur', () => {
        if (kind !== 'price' || input.value === '') return;
        const rounded = String(closestPrice(input.value));
        if (rounded !== input.value) { input.value = rounded; onChange(rounded, true); }
      });
      control.append(decrement, input, increment); wrap.append(control); return wrap;
    }
    const panel = (title, body, className) => {
      const section = el('section', 'af-demo-panel afp-bid-panel' + (className ? ' ' + className : ''));
      if (title) section.append(el('h3', '', title));
      section.append(body);
      return section;
    };
    function addFilter() {
      if (data.profiles.length >= MAX_FILTERS) return;
      const next = clone(data);
      const profile = { ...clone(next.profiles.at(-1)), id: makeId(), name: `Filter ${next.profiles.length + 1}`, order: next.profiles.length, enabled: false, filter: null, playerRating: null, bidRules: [] };
      next.profiles.push(profile); selectedId = profile.id; deleted = null; changed(next);
    }
    function removeFilter(index) {
      if (data.profiles.length < 2) return;
      const next = clone(data);
      const [removed] = next.profiles.splice(index, 1);
      next.profiles.forEach((item, order) => { item.order = order; });
      deleted = { profile: removed, index };
      if (removed.id === selectedId) selectedId = next.profiles[Math.min(index, next.profiles.length - 1)].id;
      changed(next);
    }
    function moveFilter(index, step) {
      reorderFilter(index, index + step);
    }
    function reorderFilter(index, target) {
      if (target < 0 || target >= data.profiles.length) return;
      const next = clone(data); next.profiles.splice(target, 0, next.profiles.splice(index, 1)[0]);
      next.profiles.forEach((item, order) => { item.order = order; }); changed(next);
    }
    function reorderRule(index, target) {
      const next = clone(data); const rules = next.profiles.find(item => item.id === selectedId).bidRules;
      if (target < 0 || target >= rules.length) return;
      rules.splice(target, 0, rules.splice(index, 1)[0]); changed(next);
    }
    function pointerReorder(handle, strip, index, onDrop, focusKey) {
      let motion = null;
      let suppressClick = false;
      const tiles = () => [...strip.children].filter(node => node.classList.contains('afp-filter-tile'));
      const clear = () => {
        for (const tile of tiles()) tile.classList.remove('afp-is-dragging', 'afp-drop-target');
        motion = null;
      };
      handle.addEventListener('pointerdown', event => {
        if (event.button !== 0 || motion) return;
        motion = { pointerId: event.pointerId, startY: event.clientY, y: event.clientY, moved: false, target: index };
        handle.setPointerCapture(event.pointerId);
      });
      handle.addEventListener('pointermove', event => {
        if (!motion || event.pointerId !== motion.pointerId) return;
        motion.y = event.clientY;
        if (!motion.moved && Math.abs(event.clientY - motion.startY) < 4) return;
        motion.moved = true;
        event.preventDefault();
        const items = tiles();
        let target = items.length - 1;
        for (let candidate = 0; candidate < items.length; candidate++) {
          const bounds = items[candidate].getBoundingClientRect();
          if (event.clientY < bounds.top + bounds.height / 2) { target = candidate; break; }
        }
        motion.target = target;
        items.forEach((tile, itemIndex) => {
          tile.classList.toggle('afp-is-dragging', itemIndex === index);
          tile.classList.toggle('afp-drop-target', itemIndex === target && target !== index);
        });
        const bounds = strip.getBoundingClientRect();
        if (event.clientY < bounds.top + 25) strip.scrollTop -= 8;
        else if (event.clientY > bounds.bottom - 25) strip.scrollTop += 8;
      });
      handle.addEventListener('pointerup', event => {
        if (!motion || event.pointerId !== motion.pointerId) return;
        const { moved, target } = motion;
        if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
        clear();
        if (moved) {
          suppressClick = true;
          setTimeout(() => { suppressClick = false; }, 0);
          if (target !== index) onDrop(index, target);
          event.preventDefault();
        }
      });
      handle.addEventListener('pointercancel', event => {
        if (motion && event.pointerId === motion.pointerId) clear();
      });
      handle.addEventListener('lostpointercapture', () => { if (motion) clear(); });
      handle.addEventListener('click', event => {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault(); event.stopImmediatePropagation();
      }, true);
      handle.addEventListener('keydown', event => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        event.preventDefault();
        const target = index + (event.key === 'ArrowUp' ? -1 : 1);
        if (target < 0 || target >= tiles().length) return;
        onDrop(index, target);
        requestAnimationFrame(() => {
          const replacement = [...host.querySelectorAll('.afp-filter-select')].find(item => item.dataset.reorderKey === focusKey);
          replacement?.focus();
        });
      });
    }
    function importFilter() {
      const profile = selected();
      dialog(profile.filter ? 'Re-Import Bidding Filter' : 'Import Bidding Filter', 'Choose search criteria for this preview.', [
        { key: 'playerName', label: 'Player Name', value: profile.filter?.playerName || '' },
        { key: 'rating', label: 'Rating', type: 'number', value: profile.playerRating || '' },
        { key: 'maxBuyNow', label: 'Max Buy Now', type: 'number', value: profile.filter?.prices?.[3] || '', stepper: 'price' }
      ], 'Import Filter', ({ playerName, rating, maxBuyNow }) => {
        if (!playerName.trim()) throw new Error('Enter a player name.');
        const filter = { playerName: playerName.trim(), prices: ['', '', '', String(maxBuyNow || '')], preview: true };
        updateFilter('filter', filter, false);
        updateFilter('playerRating', rating || null, false);
        feedback('Filter imported for this preview.', 'success');
      });
    }
    function filterTitle(profile) {
      const name = String(profile.name || '').trim();
      return (name === 'First filter' || /^Filter \d+$/.test(name)) && profile.filter?.playerName ? profile.filter.playerName : name;
    }
    function arrow(label, direction, click, disabled) {
      const control = button('', null, click, 'afp-filter-arrow');
      const vector = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); vector.setAttribute('viewBox', '0 0 18 18'); vector.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', direction < 0 ? 'M9 14V4M4 9l5-5 5 5' : 'M9 4v10m-5-5 5 5 5-5');
      path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.5'); path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round');
      vector.append(path); control.append(vector);
      control.setAttribute('aria-label', label); control.disabled = disabled; return control;
    }
    function filtersView() {
      const body = el('div', 'afp-bid-view');
      const listing = el('section', 'afp-filter-list');
      const heading = el('div', 'afp-filter-heading');
      heading.append(el('h3', '', `Filters (${data.profiles.length})`));
      const add = button('Add', 'open', addFilter); add.disabled = data.profiles.length >= MAX_FILTERS; heading.append(add); listing.append(heading);
      const strip = el('div', 'afp-filter-strip');
      data.profiles.forEach((profile, index) => {
        const tile = el('div', 'afp-filter-tile'); tile.setAttribute('aria-selected', String(profile.id === selectedId));
        tile.append(el('span', 'afp-filter-number', `${index + 1}.`));
        const choose = el('button', 'afp-filter-select'); choose.type = 'button'; choose.dataset.reorderKey = profile.id; choose.setAttribute('aria-pressed', String(profile.id === selectedId));
        choose.append(document.createTextNode(filterTitle(profile) + (profile.enabled ? '' : ' (Disabled)')));
        const pages = Number(profile.pageLimit) > 0 ? `After ${Number(profile.pageLimit)} page${Number(profile.pageLimit) === 1 ? '' : 's'}` : 'After final page';
        choose.append(el('small', 'afp-filter-summary', `${profile.filter ? '' : 'Import needed / '}${pages}`));
        choose.addEventListener('click', () => { selectedId = profile.id; render(); }); tile.append(choose);
        const tools = el('div', 'afp-filter-tools');
        tools.append(arrow(`Move filter ${index + 1} up`, -1, () => moveFilter(index, -1), index === 0));
        tools.append(arrow(`Move filter ${index + 1} down`, 1, () => moveFilter(index, 1), index === data.profiles.length - 1));
        const remove = button('', 'delete', () => removeFilter(index), 'afp-filter-delete'); remove.disabled = data.profiles.length === 1; remove.setAttribute('aria-label', `Delete filter ${index + 1}`); tools.append(remove); tile.append(tools);
        const toggleLabel = el('label', 'afp-bid-enable'); const toggle = el('input'); toggle.type = 'checkbox'; toggle.checked = !!profile.enabled; toggle.setAttribute('aria-label', `Enable filter ${index + 1}`);
        toggle.addEventListener('change', () => { const next = clone(data); next.profiles[index].enabled = toggle.checked; changed(next); });
        toggleLabel.append(toggle); tile.append(toggleLabel); strip.append(tile);
        pointerReorder(choose, strip, index, reorderFilter, profile.id);
      });
      listing.append(strip); body.append(listing);
      if (deleted) {
        const undo = el('p', 'afp-bid-undo', `${deleted.profile.name} removed. `);
        undo.append(button('Undo', null, () => { const next = clone(data); next.profiles.splice(deleted.index, 0, deleted.profile); next.profiles.forEach((profile, index) => { profile.order = index; }); selectedId = deleted.profile.id; deleted = null; changed(next); }));
        body.append(undo);
      }
      const profile = selected();
      const editor = el('section', 'af-demo-panel afp-bid-editor');
      const editorHeading = el('div', 'afp-bid-editor-heading');
      const nameWrap = el('label', 'afp-bid-name'); nameWrap.append(el('span', '', `${data.profiles.indexOf(profile) + 1}.`));
      const name = el('input', 'afp-input'); name.type = 'text'; name.value = profile.name; name.maxLength = 80; name.setAttribute('aria-label', 'Filter name');
      name.addEventListener('input', () => updateFilter('name', name.value, false)); nameWrap.append(name); editorHeading.append(nameWrap);
      editorHeading.append(button(profile.filter ? 'Re-Import' : 'Import Bidding Filter', 'open', importFilter)); editor.append(editorHeading);
      const description = el('p', 'afp-bid-filter-description'); description.append(el('strong', '', 'Filter: '));
      description.append(document.createTextNode(profile.filter ? profile.filter.playerName || 'All matching players' : 'Import a search filter from the web app.')); editor.append(description);
      const priceRow = el('div', 'afp-filter-price-row');
      if (profile.filter?.playerName) priceRow.append(stepField('Rating Requirement:', profile.playerRating ?? '', (value, rerender) => updateFilter('playerRating', value, rerender), 'rating'));
      if (profile.priceMode === 'fixed') priceRow.append(stepField('Maximum Bid:', profile.maxBid, (value, rerender) => updateFilter('maxBid', value, rerender), 'price'));
      if (priceRow.childElementCount) editor.append(priceRow);
      editor.append(pricingDisclosure(profile), rulesDisclosure(profile)); body.append(editor);
      return body;
    }
    function pricingDisclosure(profile) {
      const details = el('details', 'afp-disclosure'); details.open = disclosures.pricing;
      details.addEventListener('toggle', () => { disclosures.pricing = details.open; });
      const summary = el('summary'); summary.append(el('h3', '', 'Pricing & Limits')); details.append(summary);
      const inside = el('div', 'afp-disclosure-body afp-bid-field-grid');
      if (profile.filter) inside.append(el('p', 'afp-bid-help', `Min bid: ${profile.filter.prices?.[0] || 'Any'} · Max bid: ${profile.filter.prices?.[1] || 'Any'} · Min Buy Now: ${profile.filter.prices?.[2] || 'Any'} · Max Buy Now: ${profile.filter.prices?.[3] || 'Any'}`));
      inside.append(field('Bid Ceiling', profile.priceMode, (value, rerender) => updateFilter('priceMode', value, rerender), { options: [['fixed', 'Fixed maximum'], ['futbin', 'FUTBIN discount']], help: 'Player bid rules can override this filter’s ceiling.' }));
      if (profile.priceMode === 'futbin') inside.append(field('FUTBIN Discount (%)', profile.discountPercent, (value, rerender) => updateFilter('discountPercent', value, rerender), { help: 'Use the exact card and platform price. Missing prices block bids.' }));
      const fields = [
        ['maxTimeRemainingSeconds', 'Time Left to Bid (s)', 'Only bid on auctions ending within this time.'],
        ['bidLimit', 'Active Auction Limit', 'Limit live managed auctions for this filter. Won, lost or removed auctions free slots.'],
        ['wonLimit', 'Items Won Limit', 'Stop new bids for this filter after this many wins. Outstanding auctions can exceed the limit. 0 means no limit.'],
        ['bidGapMs', 'Bid Spacing (ms)', 'Minimum time between bids for this filter. New bids add a short pause; defence follows auction deadlines.'],
        ['pageLimit', 'Search Page Limit', 'Pages to scan per filter pass. 0 scans until results end.']
      ];
      for (const [key, label, help] of fields) inside.append(field(label, profile[key], (value, rerender) => updateFilter(key, value, rerender), { help }));
      details.append(inside); return details;
    }
    function ruleSummary(rule) {
      const labels = { name: 'Name', cardId: 'Card ID', rating: 'Rating', position: 'Position' };
      const filled = Object.entries(rule.conditions || {}).filter(([, value]) => String(value || '').trim()).map(([key, value]) => `${labels[key] || key}: ${value}`);
      return `${filled.join(' / ') || 'Add a condition'} / Maximum bid: ${rule.maxBid || 0} coins`;
    }
    function ruleDialog(rule, creating) {
      dialog(creating ? 'New Player Bid Rule' : 'Edit Player Bid Rule', '', [
        { key: 'name', label: 'Player Name', value: rule.conditions.name },
        { key: 'cardId', label: 'Exact Card ID', value: rule.conditions.cardId },
        { key: 'rating', label: 'Rating', value: rule.conditions.rating },
        { key: 'position', label: 'Position', value: rule.conditions.position },
        { key: 'maxBid', label: 'Maximum Bid (Coins)', type: 'number', value: rule.maxBid, stepper: 'price' }
      ], creating ? 'Add Rule' : 'Save Rule', values => {
        if (!['name', 'cardId', 'rating', 'position'].some(key => values[key].trim())) throw new Error('Add at least one condition.');
        if (!Number.isInteger(Number(values.maxBid)) || Number(values.maxBid) < 150) throw new Error('Maximum bid must be at least 150 coins.');
        const next = clone(data); const target = next.profiles.find(profile => profile.id === selectedId);
        const updated = { ...rule, conditions: Object.fromEntries(['name', 'cardId', 'rating', 'position'].map(key => [key, values[key].trim()])), maxBid: Number(values.maxBid) };
        target.bidRules = creating ? [...target.bidRules, updated] : target.bidRules.map(item => item.id === rule.id ? updated : item);
        changed(next, false);
      });
    }
    function rulesDisclosure(profile) {
      const details = el('details', 'afp-disclosure'); details.open = disclosures.rules;
      details.addEventListener('toggle', () => { disclosures.rules = details.open; });
      const summary = el('summary'); summary.append(el('h3', '', 'Player Bid Rules')); details.append(summary);
      const body = el('div', 'afp-disclosure-body');
      body.append(el('p', 'afp-bid-help', 'The first enabled match sets the bid ceiling, including during defence. All filled conditions must match; names ignore accents and case. Search criteria and prices still apply.'));
      if (!profile.bidRules.length) body.append(el('p', '', 'No rules. Matching cards use this filter’s bid ceiling.'));
      const ruleStrip = el('div', 'afp-bid-rule-strip');
      profile.bidRules.forEach((rule, index) => {
        const tile = el('div', 'afp-filter-tile afp-bid-rule'); tile.append(el('span', 'afp-filter-number', `${index + 1}.`));
        const edit = el('button', 'afp-filter-select'); edit.type = 'button'; edit.dataset.reorderKey = rule.id; edit.setAttribute('aria-label', `Edit Player Bid Rule ${index + 1}`);
        edit.append(document.createTextNode(`Player Bid Rule ${index + 1}${rule.enabled ? '' : ' (Disabled)'}`), el('small', 'afp-filter-summary', ruleSummary(rule)));
        edit.addEventListener('click', () => ruleDialog(rule, false)); tile.append(edit);
        const tools = el('div', 'afp-filter-tools');
        tools.append(arrow(`Move Player Bid Rule ${index + 1} up`, -1, () => reorderRule(index, index - 1), index === 0));
        tools.append(arrow(`Move Player Bid Rule ${index + 1} down`, 1, () => reorderRule(index, index + 1), index === profile.bidRules.length - 1));
        const remove = button('', 'delete', () => { const next = clone(data); next.profiles.find(item => item.id === selectedId).bidRules.splice(index, 1); changed(next); }, 'afp-filter-delete');
        remove.setAttribute('aria-label', `Delete Player Bid Rule ${index + 1}`); tools.append(remove); tile.append(tools);
        const toggleLabel = el('label', 'afp-bid-enable'); const toggle = el('input'); toggle.type = 'checkbox'; toggle.checked = !!rule.enabled;
        toggle.setAttribute('aria-label', `Enable Player Bid Rule ${index + 1}`);
        toggle.addEventListener('change', () => { const next = clone(data); next.profiles.find(item => item.id === selectedId).bidRules[index].enabled = toggle.checked; changed(next); });
        toggleLabel.append(toggle); tile.append(toggleLabel); ruleStrip.append(tile);
        pointerReorder(edit, ruleStrip, index, reorderRule, rule.id);
      });
      body.append(ruleStrip);
      body.append(button('New Rule', 'add', () => ruleDialog({ id: makeId(), enabled: true, conditions: { name: '', cardId: '', rating: '', position: '' }, maxBid: Math.max(150, Number(profile.maxBid) || 150) }, true)));
      details.append(body); return details;
    }
    function stoppingView() {
      const body = el('div', 'afp-bid-view');
      const timing = el('div', 'afp-range-row');
      timing.setAttribute('role', 'group'); timing.setAttribute('aria-label', 'Total Duration');
      timing.append(el('strong', '', 'Total Duration'));
      const min = el('input', 'afp-input'); min.type = 'number'; min.min = '0'; min.value = String(data.loop.runDurationMinValue); min.setAttribute('aria-label', 'Total Duration Minimum');
      min.addEventListener('input', () => updateLoop('runDurationMinValue', min.value, false));
      const max = el('input', 'afp-input'); max.type = 'number'; max.min = '0'; max.value = String(data.loop.runDurationMaxValue); max.setAttribute('aria-label', 'Total Duration Maximum');
      max.addEventListener('input', () => updateLoop('runDurationMaxValue', max.value, false));
      const dash = el('span', '', '–'); dash.setAttribute('aria-hidden', 'true');
      const units = el('select', 'afp-select'); units.setAttribute('aria-label', 'Total Duration Unit');
      for (const [value, label] of [['second', 'seconds'], ['minute', 'minutes'], ['hour', 'hours']]) {
        const option = el('option', '', label); option.value = value; option.selected = data.loop.runDurationUnit === value; units.append(option);
      }
      units.addEventListener('change', () => updateLoop('runDurationUnit', units.value, false));
      timing.append(min, dash, max, units);
      body.append(panel('Timing', timing));
      const stopping = el('div', 'afp-bid-field-grid');
      stopping.append(field('Maximum Active Auctions', data.loop.maxActiveAuctions, (value, rerender) => updateLoop('maxActiveAuctions', value, rerender), { help: 'Pause new bids at this limit, or sooner when auctions need attention.' }));
      stopping.append(field('Minimum Coin Balance', data.loop.minimumCoins, (value, rerender) => updateLoop('minimumCoins', value, rerender), { help: 'Stop at or below this balance. 0 means no limit.' }));
      body.append(panel('Stopping', stopping));
      body.append(el('p', 'afp-bid-help', 'At the time limit, stop new bids and settle managed auctions. The coin limit applies throughout the run; 0 disables it.'));
      body.append(el('p', 'afp-bid-help', 'Win limits stop new bids. Outstanding auctions can still win and exceed those limits by up to the active-auction limit.'));
      return body;
    }
    function winsView() {
      const body = el('div', 'afp-bid-view');
      const fields = el('div', 'afp-bid-field-grid');
      fields.append(field('Won Auction Action', data.loop.wonAction, (value, rerender) => updateLoop('wonAction', value, rerender), { options: [['transferList', 'Store in Transfer List'], ['retain', 'Keep in Transfer Targets']], help: 'Choose where to keep confirmed wins.' }));
      if (data.loop.wonAction === 'retain') fields.append(field('Retained Wins Limit', data.loop.retainedWinsCap, (value, rerender) => updateLoop('retainedWinsCap', value, rerender), { help: 'Stop after this many wins are kept in Transfer Targets.' }));
      body.append(panel('Won Auctions', fields));
      body.append(el('p', 'afp-bid-help', 'Won, lost or removed auctions free slots in the filter’s active-auction limit. Each listing is attempted once per run.'));
      body.append(el('p', 'afp-bid-help', 'Storing a win does not list it for sale. Manual auctions are never rebid or removed.'));
      return body;
    }
    function validate() {
      const issues = [];
      if (!data.profiles.length) issues.push('Add at least one bidder profile.');
      if (data.profiles.length > MAX_FILTERS) issues.push(`A bidding plan supports up to ${MAX_FILTERS} filters.`);
      for (const profile of data.profiles) {
        if (!String(profile.name || '').trim()) issues.push('Every profile needs a name.');
        if (profile.enabled && !profile.filter) issues.push(`${profile.name}: import a current search filter.`);
        if (profile.enabled && profile.priceMode === 'fixed' && Number(profile.maxBid) < 150) issues.push(`${profile.name}: maximum bid must be at least 150.`);
        for (const [key, lower] of [['maxTimeRemainingSeconds', 1], ['bidLimit', 1], ['wonLimit', 0], ['bidGapMs', 0], ['pageLimit', 0]]) {
          if (!Number.isInteger(Number(profile[key])) || Number(profile[key]) < lower) issues.push(`${profile.name}: ${key} must be ${lower} or more.`);
        }
        if (profile.priceMode === 'futbin' && (Number(profile.discountPercent) < 0 || Number(profile.discountPercent) >= 100)) issues.push(`${profile.name}: discount must be from 0 to less than 100.`);
      }
      if (Number(data.loop.runDurationMinValue) > 0 && Number(data.loop.runDurationMaxValue) > 0 && Number(data.loop.runDurationMinValue) > Number(data.loop.runDurationMaxValue)) issues.push('Minimum run duration must not exceed the maximum.');
      if (Number(data.loop.maxActiveAuctions) < 1) issues.push('Maximum Active Auctions must be at least 1.');
      if (Number(data.loop.minimumCoins) < 0) issues.push('Minimum Coin Balance must be 0 or more.');
      return issues;
    }
    async function save() {
      const issues = validate();
      if (issues.length) { feedback(issues.join(' '), 'error'); return false; }
      try {
        const result = options.saveStored?.('bidding', clone(data));
        if (result && typeof result.then === 'function') await result;
        snapshot = clone(data); savedPulse = Date.now();
        feedback(activeKey() ? 'Profile saved and activated for bidding.' : 'Saved.', 'success');
        render(); return true;
      } catch (issue) { feedback(issue?.message || 'Could not save bidding settings.', 'error'); return false; }
    }
    function revert() {
      data = clone(snapshot); selectedId = data.profiles[0].id; deleted = null; savedPulse = 0;
      render(); feedback('Saved bidding settings restored.', 'success'); return clone(data);
    }
    function setData(next) { data = normalize(next); snapshot = clone(data); selectedId = data.profiles[0].id; deleted = null; status = ''; error = ''; render(); }
    function confirmNavigation(next) {
      if (!hasUnsaved()) { next?.(); return; }
      closeModal();
      modalFocus = document.activeElement;
      const backdrop = el('div', 'afp-modal-backdrop');
      const panel = el('section', 'afp-modal');
      panel.setAttribute('role', 'alertdialog'); panel.setAttribute('aria-modal', 'true');
      const heading = el('h2', '', 'Unsaved Changes'); heading.id = 'afp-bid-unsaved-' + makeId();
      panel.setAttribute('aria-labelledby', heading.id); panel.append(heading);
      const actions = el('div', 'afp-modal-actions');
      actions.append(button('Keep Editing', 'close', closeModal));
      actions.append(button('Discard Changes', 'revert', () => { closeModal(); next?.(); }));
      actions.append(button('Save and Continue', 'confirm', async () => { if (await save()) { closeModal(); next?.(); } }, 'afp-button-primary'));
      panel.append(actions); backdrop.append(panel); shell.append(backdrop); modal = backdrop;
      backdrop.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); closeModal(); }
        if (event.key === 'Tab') {
          const items = [...actions.querySelectorAll('button')]; const index = items.indexOf(document.activeElement);
          if (event.shiftKey && index === 0) { event.preventDefault(); items.at(-1).focus(); }
          else if (!event.shiftKey && index === items.length - 1) { event.preventDefault(); items[0].focus(); }
        }
      });
      actions.querySelector('button')?.focus();
    }
    function render() {
      const oldBody = host.querySelector('.af-demo-workspace');
      const scrollTop = oldBody?.scrollTop || 0;
      if (screen) [...host.children].filter(node => !node.classList.contains('af-demo-nav')).forEach(node => node.remove());
      else host.replaceChildren();
      const tabs = el('div', 'af-demo-tabs afp-bid-tabs');
      const tabList = [
        ['filters', 'Filters'], ['run', 'Stopping'], ['wins', 'After Winning']
      ];
      const filterTab = button('Filters', null, () => { activeTab = 'filters'; render(); });
      const runTabs = el('div'); runTabs.append(el('small', '', 'Run Settings'));
      const runButtons = el('span');
      for (const [id, label] of tabList.slice(1)) runButtons.append(button(label, null, () => { activeTab = id; render(); }));
      runTabs.append(runButtons); tabs.append(filterTab, runTabs);
      tabs.querySelectorAll('button').forEach((control, index) => { control.classList.toggle('afp-tab-active', tabList[index][0] === activeTab); control.setAttribute('aria-selected', String(tabList[index][0] === activeTab)); });
      const workspace = el('div', 'af-demo-workspace afp-bid-workspace');
      workspace.append(tabs, activeTab === 'filters' ? filtersView() : activeTab === 'run' ? stoppingView() : winsView());
      const feedbackNode = el('p', 'afp-bid-feedback', error || status); feedbackNode.dataset.tone = error ? 'error' : 'info'; feedbackNode.setAttribute('role', error ? 'alert' : 'status'); workspace.append(feedbackNode);
      const footer = el('div', 'af-demo-run-footer afp-bid-footer');
      const actions = el('div', 'afp-bid-actions');
      const help = button('', 'help', () => { helpVisible = !helpVisible; render(); }); help.setAttribute('aria-label', helpVisible ? 'Hide Help' : 'Show Help'); help.title = help.getAttribute('aria-label'); actions.append(help);
      const saveButton = button('Save Settings', 'save', save, 'afp-bid-save'); if (savedPulse) saveButton.dataset.state = 'saved'; actions.append(saveButton);
      const revertButton = button('', 'revert', revert); revertButton.setAttribute('aria-label', 'Revert'); revertButton.title = 'Revert to saved settings'; actions.append(revertButton);
      if (activeKey()) {
        const remove = button('', 'delete', () => dialog('Remove Profile', 'Delete this saved bidding profile permanently.', [], 'Remove Profile', async () => { await options.onRemoveProfile?.('bidding', activeKey()); feedback('Profile removed.', 'success'); }), 'afp-button-danger');
        remove.setAttribute('aria-label', 'Remove Profile'); actions.append(remove);
      }
      footer.append(actions);
      const start = button('Start Bidding', null, () => {}, 'afp-bid-start');
      start.prepend(el('span', 'af-demo-play-icon')); start.disabled = true; start.title = 'Open FUTPilot to start bidding'; footer.append(start);
      host.append(workspace, footer);
      host.classList.toggle('afp-help-visible', helpVisible);
      workspace.scrollTop = scrollTop;
      return host;
    }
    render();
    return { getData: () => clone(data), setData, hasUnsaved, save, revert, render, confirmNavigation, destroy: closeModal };
  }
  window.FutpilotPreviewBidding = { defaultState, mount };
})();
